/* eslint-disable @typescript-eslint/no-unsafe-member-access */
/* eslint-disable @typescript-eslint/no-unsafe-assignment */
/* eslint-disable no-lonely-if */
/* eslint-disable no-unused-vars */
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { Box, InlineMessage, MenuInput } from '@saviynt/design-system';

import Logger from '../../../../services/loggerService';

import './dropdown.css';

export interface DropdownOption {
  label: string;
  value: string;
}

export interface DropdownConfig {
  debounceMs?: number;
  pageSize?: number;
  enableInfiniteScroll?: boolean;
}

export interface FormDropdownProps {
  type?: unknown;
  attributeName: string;
  payloadAtrributeName: string;
  options?: DropdownOption[];
  required?: boolean;
  fieldDisabled?: boolean;
  placeholder?: string;
  hasMoreOptions?: boolean;
  isLoading?: boolean;
  config?: DropdownConfig;
  attributeKey?: string;
  apiURL?: string;
  errorMessage?: string | null;
  value?: string;

  // Generic API function provided by parent
  fetchData?: (params: {
    offset: number;
    max: number;
    findBy: string;
  }) => Promise<DropdownOption[]>;

  inputValueStatus: (status: {
    attributeName: string;
    value: string | { id: string; value: string };
    hasError: boolean;
    payloadAtrributeName: string;
  }) => void;
}

/**
 * dropdown
 */
function FormDropdown({
  type,
  attributeName,
  placeholder,
  payloadAtrributeName,
  options: initialOptions,
  required = false,
  fieldDisabled = false,
  inputValueStatus,
  hasMoreOptions: _hasMoreOptions,
  isLoading: externalIsLoading,
  config = {},
  fetchData,
  attributeKey,
  apiURL,
  errorMessage,
  value,
}: FormDropdownProps) {
  const [options, setOptions] = useState<DropdownOption[]>(
    initialOptions ?? []
  );
  const [searchTerm, setSearchTerm] = useState('');
  const [offset, setOffset] = useState(0);
  const offsetRef = useRef(0);
  const [hasMore, setHasMore] = useState(false);
  const [isSearching, setIsSearching] = useState(false);
  const [hasInitialLoad, setHasInitialLoad] = useState(false);

  const requestIdRef = useRef(0);

  const {
    debounceMs = 500,
    pageSize = 25,
    enableInfiniteScroll = true,
  } = config;

  const parseValue = (raw: string | undefined) => {
    if (!raw) {
      return null;
    }

    // If it's already an object, return it directly
    if (typeof raw === 'object' && raw !== null) {
      return raw as { id: string; value: string };
    }

    try {
      const parsed = JSON.parse(raw);

      // eslint-disable-next-line @typescript-eslint/no-unsafe-return
      return parsed.id && parsed.value ? parsed : { id: raw, value: raw };
    } catch {
      return { id: raw, value: raw };
    }
  };

  // eslint-disable-next-line @typescript-eslint/no-unsafe-return
  const valueObj = useMemo(() => parseValue(value), [value]);

  useEffect(() => {
    // Only fetch data for UserGroup dropdown when it's actually needed
    if (fetchData && attributeName === 'endpoints.ownerkey') {
      // Check if UserGroup is selected by looking at the form context
      // This prevents unnecessary API calls when User is selected
      const shouldFetch = true; // Will be controlled by parent component visibility

      if (shouldFetch) {
        fetchData({
          offset: 0,
          max: pageSize,
          findBy: '',
        })
          .then((initialPaginatedData) => {
            setOptions(initialPaginatedData);
            setHasMore(initialPaginatedData.length > 0);
            setHasInitialLoad(true);
            offsetRef.current = 1; // Set to 1 after initial load

            if (value) {
              const existingOption = initialPaginatedData.find(
                (opt) => opt.value === valueObj.id
              );

              if (!existingOption) {
                // Search through pages until match is found
                const searchForMatch = async (
                  currentOffset = 0
                ): Promise<boolean> => {
                  try {
                    const searchOptions = await fetchData({
                      offset: currentOffset,
                      max: pageSize,
                      findBy: valueObj.value,
                    });

                    const matchingOption = searchOptions.find(
                      (opt) => opt.value === valueObj.id
                    );

                    if (matchingOption) {
                      // Check if option already exists to prevent duplicates
                      setOptions((prev) => {
                        const exists = prev.some(
                          (opt) => opt.value === matchingOption.value
                        );

                        return exists ? prev : [matchingOption, ...prev];
                      });

                      return true; // Found match, stop searching
                    }

                    if (searchOptions.length === pageSize) {
                      // Continue searching if we got a full page
                      return await searchForMatch(currentOffset + 1);
                    }

                    return false; // No more results
                  } catch (error) {
                    Logger.log('Search error:', error);

                    return false;
                  }
                };

                // eslint-disable-next-line @typescript-eslint/no-floating-promises
                searchForMatch();
              }
            }
          })
          .catch(() => {});
      }
    } else if (fetchData && attributeName !== 'endpoints.ownerkey') {
      // For other dropdowns, fetch normally
      fetchData({
        offset: 0,
        max: pageSize,
        findBy: '',
      })
        .then((initialPaginatedData) => {
          setOptions(initialPaginatedData);
          setHasMore(initialPaginatedData.length > 0);
          setHasInitialLoad(true);
          offsetRef.current = 1;
        })
        .catch(() => {});
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadData = useCallback(
    async (reset = false, currentSearchTerm = searchTerm) => {
      if (!fetchData) {
        // Fallback to initial options if no fetchData provided
        if (initialOptions) {
          setOptions(initialOptions);
          setHasMore(false);
        }

        return;
      }

      try {
        const currentOffset = reset ? 0 : offsetRef.current;

        requestIdRef.current += 1;
        const requestId = requestIdRef.current;

        const newOptions = await fetchData({
          offset: currentOffset,
          max: pageSize,
          findBy: currentSearchTerm,
        });

        // Only update if this is still the latest request
        if (requestId === requestIdRef.current) {
          setOptions((prev) => (reset ? newOptions : [...prev, ...newOptions]));
          setHasMore(enableInfiniteScroll && newOptions.length > 0);
          setHasInitialLoad(true);

          const newOffset = currentOffset + 1;

          setOffset(newOffset);
          offsetRef.current = newOffset;
        }
      } catch (error) {
        // Error handled silently
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [fetchData, searchTerm, pageSize, enableInfiniteScroll, initialOptions]
  );

  const debounceRef = useRef<NodeJS.Timeout>();

  const debouncedSearch = useCallback(
    (term: string) => {
      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
      }

      debounceRef.current = setTimeout(() => {
        setSearchTerm(term);

        if (fetchData) {
          // Server-side search with API
          setHasInitialLoad(false);
          setOffset(0);
          offsetRef.current = 0;
          loadData(true, term)
            .catch(() => {})
            .finally(() => setIsSearching(false));
        } else {
          if (!term) {
            setOptions(initialOptions ?? []);
          } else {
            const filtered = (initialOptions ?? []).filter((option) =>
              option.label.toLowerCase().includes(term.toLowerCase())
            );

            setOptions(filtered);
          }
        }
      }, debounceMs);
    },
    [fetchData, loadData, initialOptions, debounceMs]
  );

  const handleSearch = useCallback(
    (term: string) => {
      // Clear Redux state when user starts typing
      if (attributeName === 'endpoints.ownerkey' && term !== searchTerm) {
        inputValueStatus({
          attributeName,
          value: '',
          hasError: required,
          payloadAtrributeName,
        });
      }

      if (fetchData && term) {
        setIsSearching(true);
      }

      debouncedSearch(term);
    },
    [
      debouncedSearch,
      fetchData,
      attributeName,
      inputValueStatus,
      required,
      payloadAtrributeName,
      searchTerm,
    ]
  );

  const sharedCritMessage = (
    <InlineMessage kind='missingRequiredField' size='small' />
  );

  const handleLoadMore = useCallback(
    (event: React.UIEvent<HTMLElement>) => {
      if (!enableInfiniteScroll || !fetchData) {
        return;
      }

      const { scrollTop, scrollHeight, clientHeight } = event.currentTarget;
      const isNearBottom = scrollTop + clientHeight >= scrollHeight - 5;

      if (isNearBottom && hasMore && !externalIsLoading) {
        loadData(false).catch(() => {});
      }
    },
    [enableInfiniteScroll, fetchData, hasMore, externalIsLoading, loadData]
  );

  const handleChange = (selected: DropdownOption | null) => {
    let newValue: string | { id: string; value: string };

    if (attributeName === 'endpoints.ownerkey' && selected) {
      newValue = {
        id: selected.value,
        value: selected.label,
      };
    } else {
      newValue = selected?.value ?? '';
    }

    inputValueStatus({
      attributeName,
      value: newValue,
      hasError: required ? !newValue : false,
      payloadAtrributeName,
    });
  };

  // Debug log for error message
  if (attributeName === 'endpoints.ownerkey') {
    // 'SingleSelect errorMessage:', errorMessage
  }

  return (
    <Box className='FormBuilder-DropdownWrapper'>
      <MenuInput
        options={options.map((option) => ({
          ...option,
          isChecked: option.value === valueObj?.id,
        }))}
        selectedOptions={
          valueObj ? [{ label: valueObj.value, value: valueObj.id }] : []
        }
        onChange={handleChange}
        inputSize='large'
        isRequired={required}
        isDisabled={fieldDisabled}
        placeholder={placeholder}
        CriticalHelperText={sharedCritMessage}
        size='large'
        isMulti={false}
        isSearchDisabled
        hasMoreOptions={hasMore}
        isLoading={externalIsLoading || isSearching}
        searchTerm={searchTerm}
        onSearchTermChange={handleSearch}
        onInputReset={() => {
          if (attributeName === 'endpoints.ownerkey') {
            inputValueStatus({
              attributeName,
              value: '',
              hasError: required,
              payloadAtrributeName,
            });
          }
        }}
        onScroll={handleLoadMore}
        minOptionsForSearch={0}
        menuItemHeightConfigs={{
          maxHeight: '200px',
        }}
        dropdownClassName='force-scroll-dropdown'
      />
      {errorMessage && (
        <div style={{ color: 'red', fontSize: '12px', marginTop: '4px' }}>
          {errorMessage}
        </div>
      )}
    </Box>
  );
}

export default FormDropdown;

FormDropdown.defaultProps = {
  type: undefined,
  placeholder: 'Select an option',
  fieldDisabled: false,
  required: false,
  options: [],
  hasMoreOptions: false,
  isLoading: false,
  config: {},
  fetchData: undefined,
  attributeKey: undefined,
  apiURL: undefined,
  errorMessage: null,
  value: undefined,
};
